import React from "react";
import { Alert, AlertTitle, Box, Button, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

/**
 * Tells the viewer that the KYC step did not complete, why, and what to do.
 *
 * The automation writes `kycStatus` on the policy:
 *   - Reliance: when the document upload starts it sets "KYC was started but did not finish" and clears it only when
 *     Reliance accepts SUBMIT, replacing it with the specific reason when it stops — so it is present for every way
 *     the step can end badly. A Reliance policy always carries `kycUrls.uploadUrl` (the Document Upload Page), which is
 *     what the operator uses to upload the Aadhaar by hand.
 *   - KSHEMA: the portal's own words when it refused the KYC (no `kycUrls`). Its wording is unchanged.
 *
 * Nothing is shown while the job is still `processing` — the run is inside the KYC step right now.
 *
 * When the KYC did not work the Reliance automation also goes on to the proposal's payment page and saves it
 * (policy.paymentLink — the "Make Payment Page" card). It pays nothing and sends the customer nothing, so once the
 * document is in, the operator pays from that link: the notice says so.
 *
 * @param {string} kycStatus       policy.kycStatus
 * @param {string} uploadUrl       policy.kycUrls.uploadUrl
 * @param {string} proposalNumber  policy.proposalNumber (already saved at the insurer)
 * @param {string} jobStatus       policy.jobStatus
 * @param {string} paymentUrl      policy.paymentLink — the payment page the automation reached after the KYC failed
 */
const KycFailedNotice = ({ kycStatus, uploadUrl, proposalNumber, jobStatus, paymentUrl }) => {
  const reason = String(kycStatus || "").trim();
  if (!reason) return null;
  if (jobStatus === "processing") return null;
  // A COMPLETED policy got through its KYC (a KYC that does not work ends the run as a failure), so a note still on it is
  // from an earlier attempt that was fixed by editing and running again — the automation now clears it, and this hides
  // the ones that were left behind before it did.
  if (jobStatus === "completed") return null;

  // No Document Upload Page was captured: this is the portal's own refusal (KSHEMA) — the original notice.
  if (!uploadUrl) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
          mb: 2,
          px: 2,
          py: 1.5,
          borderRadius: "10px",
          border: "1px solid #f2c9c4",
          borderLeft: "4px solid #b3261e",
          backgroundColor: "#fdf6f5",
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 20, color: "#b3261e", mt: "1px" }} />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#b3261e" }}>
            KYC not completed — the portal said "{reason}"
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#667085", mt: 0.25 }}>
            Check the customer's Aadhaar number and name against their card, then open the KYC page below to finish
            it.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Alert
      severity="error"
      icon={<ErrorOutlineIcon sx={{ color: "#b3261e" }} />}
      sx={{
        mb: 2,
        alignItems: "flex-start",
        backgroundColor: "#fdf6f5",
        border: "1px solid #f2c9c4",
        borderLeft: "4px solid #b3261e",
        color: "#7a1710",
      }}
    >
      <AlertTitle sx={{ fontWeight: 700, color: "#b3261e" }}>KYC failed — upload the document manually</AlertTitle>
      <Typography sx={{ fontSize: 13.5, color: "#101828" }}>{reason}</Typography>
      {proposalNumber ? (
        <Typography sx={{ fontSize: 12.5, color: "#667085", mt: 0.5 }}>
          The proposal {proposalNumber} is already saved at Reliance — only the KYC is left.
        </Typography>
      ) : null}
      {paymentUrl ? (
        <Typography sx={{ fontSize: 12.5, color: "#667085", mt: 0.5 }}>
          Nothing was paid and no payment link was sent to the customer.
        </Typography>
      ) : null}
      <Box component="ol" sx={{ m: 0, mt: 1, pl: 2.5, fontSize: 12.5, color: "#344054" }}>
        <li>Open the Document Upload Page (the button below, or the card in Process Links).</li>
        <li>Upload the customer's Aadhaar — front and back — in Identity and Address Verification.</li>
        <li>Press SUBMIT, then PROCEED.</li>
        {paymentUrl ? <li>Then pay the proposal from the Make Payment Page (below).</li> : null}
      </Box>
      <Button
        size="small"
        variant="contained"
        color="error"
        startIcon={<OpenInNewIcon />}
        onClick={() => window.open(uploadUrl, "_blank", "noopener,noreferrer")}
        sx={{ mt: 1.5, textTransform: "none", boxShadow: "none" }}
      >
        Open Document Upload Page
      </Button>
    </Alert>
  );
};

export default KycFailedNotice;
